import React from 'react';
import PropTypes from 'prop-types';

import { isGiftCardEmpty as isGiftCardEmptyFn } from '@/common/customer.gift-card';
import AddGiftCardButton from '@/common/customer.gift-card/AddGiftCardButton';

import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';

import CustomHtmlSlot from '@/common/components/CustomHtmlSlot';
import ModalHeader from '../common/modal/header';

import changeToAddGiftCard from '../customer.gift-card';

import OrderCustomer from './order-customer';
import OrderLocation from './order-location';
import OrderClosingWarning from './order-closing-warning';
import OrderAddress from './order-address';
import OrderTime from './order-time';
import OrderItems from './order-items/index';
import OrderConfirmationFooter from './order-confirmation-footer';
import OrderConfirmationPayment from './order-confirmation-payment/index';
import OrderConfirmationSuccess from './order-confirmation-success';
import OrderComment from './order-comment';
import { isPayAtTablePlatform } from '@/legacy/api/app';

// import { setupFacebook, setupApple } from '@/utils/social-login-callbacks';

const { Customer } = Mobi2Go;

class OrderConfirmation extends React.Component {
    static propTypes = {
        closeModal: PropTypes.func.isRequired,
        openModal: PropTypes.func.isRequired,
        isModalOpen: PropTypes.func.isRequired,
        scrollToBottomOfModal: PropTypes.func.isRequired,
        setModalIsClosable: PropTypes.func.isRequired,
        setModalAttribute: PropTypes.func.isRequired,
        removeModalAttribute: PropTypes.func.isRequired,
        order: PropTypes.object.isRequired,
        t: PropTypes.func.isRequired,
    };

    constructor() {
        super();

        this.order = null;
        this.is_cancelling_confirmation = false;
        this.state = {
            customer_name: '',
            customer_phone_number: '',
            customer_is_subscriber: false,
            customer_credit_balance: null,
            location: null,
            minutes_to_closing: '',
            expected_at: {},
            address: '',
            method: '',
            expected_in_string: '',
            is_expected_today: false,
            products: [],
            total: {},
            delivery_fee: {},
            discounts_total: {},
            surcharge: {},
            tip: {},
            service_fee: {},
            tax_totals: [],
            payment_method: '',
            payment_total: {},
            progress_phase: '',
            progress_status: '',
            show_payment_processing_warning: false,
            forced_into_loading_state: false,
            payment_processor: null,
            credit: null,
            comment: '',
            comment_max_length: null,
            isGuest: false,
            isGiftCardEmpty: false,
            loyalty_program_with_gift_cards: '',
        };
        this.customer_events = {
            update: this.onUpdateCustomer,
            updateCreditBalance: this.onUpdateCustomer,
        };
        this.order_events = {
            updateLocation: this.onUpdateLocation,
            updateMinutesToClosing: this.onUpdateMinutesToClosing,
            updateExpectedAt: this.onUpdateExpectedAt,
            updateAddress: this.onUpdateAddress,
            updateMethod: this.onUpdateMethod,
            updateExpectedInString: this.onUpdateExpectedInString,
            updateTotal: this.onUpdateTotal,
            updateProductsQuantity: this.onUpdateProducts,
            updateTaxTotals: this.onUpdateTaxTotals,
            addProduct: this.onUpdateProducts,
            removeProduct: this.onUpdateProducts,
            updatePaymentMethod: this.onUpdatePaymentMethod,
            updateProgressPhase: this.onUpdateProgressPhase,
            updateProgressStatus: this.onUpdateProgressStatus,
            updateCustomer: this.onUpdateOrderCustomer,
            updatePaymentProcessor: this.onUpdatePaymentProcessor,
            updateCredit: this.onUpdateCredit,
            updatePaymentTotal: this.onUpdatePaymentTotal,
            updateComment: this.onUpdateComment,
        };
    }

    componentDidMount() {
        this.setOrder(this.props.order);

        this.setState({
            loyalty_program_with_gift_cards: Customer.getEnabledLoyaltyProgramWithGiftCardsEnabled(),
        });
    }

    setOrder(order) {
        // unregister all previous events
        if (this.order !== null) {
            const customer = this.order.getCustomer();
            if (customer) {
                customer.unbind(this.customer_events);
            }
            this.order.unbind(this.order_events);
        }

        this.order = order;
        const customer = this.order.getCustomer();
        const location = this.order.getLocation();
        if (customer) {
            customer.bind(this.customer_events);
        }
        order.bind(this.order_events);

        this.setState(
            {
                customer_name: customer ? customer.name : '',
                customer_phone_number: customer ? customer.phone_number : '',
                customer_is_subscriber: customer
                    ? customer.is_subscriber
                    : false,
                customer_credit_balance: customer
                    ? customer.getCreditBalance()
                    : null,
                location,
                minutes_to_closing: order.getMinutesToClosing(),
                expected_at: order.getExpectedAt(),
                address: order.getAddress()
                    ? order.getAddress().toString()
                    : '',
                method: order.getMethod(),
                expected_in_string: order.getExpectedInString(),
                is_expected_today: order.isExpectedToday(),
                products: order.getProducts(),
                total: order.getTotal(),
                delivery_fee: order.getDeliveryFee(),
                discounts_total: order.getDiscountsTotal(),
                surcharge: order.getSurcharge(),
                service_fee: order.getServiceFee(),
                tip: order.getTip(),
                tax_totals: order.getTaxTotals(),
                payment_method: order.getPaymentMethod(),
                payment_total: order.getPaymentTotal(),
                progress_phase: order.getProgressPhase(),
                progress_status: order.getProgressStatus(),
                show_payment_processing_warning: false,
                payment_processor: order.getPaymentProcessor(),
                credit: order.getCredit(),
                comment: order.getComment(),
                comment_max_length: order.getCommentMaxLength(),
                isGuest: Customer.isGuest(),
                isGiftCardEmpty: isGiftCardEmptyFn(Customer),
            },
            () => {
                this.onProgressPhaseUpdated();
                this.setPaymentProcessorAttributes();
            }
        );
    }

    setPaymentProcessorAttributes = () => {
        this.props.setModalAttribute(
            'data-payment-method',
            this.state.payment_method
        );
        if (
            this.state.payment_processor &&
            this.state.payment_method === 'online'
        ) {
            this.props.setModalAttribute(
                'data-payment-processor',
                this.state.payment_processor.getName()
            );
            this.props.setModalAttribute(
                'data-payment-form-type',
                this.state.payment_processor.getFormType()
            );
        } else {
            this.props.removeModalAttribute('data-payment-processor');
            this.props.removeModalAttribute('data-payment-form-type');
        }
    };

    onUpdatePaymentProcessor = () => {
        this.setState(
            {
                payment_processor: this.order.getPaymentProcessor(),
            },
            this.setPaymentProcessorAttributes
        );
    };

    onUpdateOrderCustomer = () => {
        const customer = this.order.getCustomer();
        if (customer) {
            customer.bind(this.customer_events);
        }
        this.setState({
            customer_name: customer ? customer.name : '',
            customer_phone_number: customer ? customer.phone_number : '',
            customer_is_subscriber: customer ? customer.is_subscriber : false,
            customer_credit_balance: customer
                ? customer.getCreditBalance()
                : null,
            isGuest: Customer.isGuest(),
            isGiftCardEmpty: isGiftCardEmptyFn(Customer),
        });
    };

    onUpdateCustomer = () => {
        const customer = this.order.getCustomer();
        this.setState({
            customer_name: customer.name,
            customer_phone_number: customer.phone_number,
            customer_is_subscriber: customer.is_subscriber,
            customer_credit_balance: customer
                ? customer.getCreditBalance()
                : null,
            isGuest: Customer.isGuest(),
            isGiftCardEmpty: isGiftCardEmptyFn(Customer),
        });
    };

    onUpdateProgressStatus = () => {
        this.setState(
            {
                progress_status: this.order.getProgressStatus(),
            },
            () => {
                this.props.setModalAttribute(
                    'data-status',
                    this.state.progress_status
                );
                if (this.state.progress_status === 'unconfirmable') {
                    this.props.closeModal();
                }
            }
        );
    };

    onProgressPhaseUpdated = () => {
        const { progress_phase } = this.state;

        this.props.setModalAttribute('data-phase', progress_phase);

        if (progress_phase === 'unconfirmed') {
            this.props.setModalIsClosable(true);
        }
        if (progress_phase === 'confirming' || progress_phase === 'confirmed') {
            this.props.setModalIsClosable(false);
            this.forceModalOpenUntilUnconfirmed();
        }
        if (progress_phase === 'confirming') {
            this.showPaymentProcessingWarning();
        } else {
            this.hidePaymentProcessingWarning();
            this.is_cancelling_confirmation = false;
        }
        setTimeout(this.showBottomIfPaymentIframe, 100);
    };

    showBottomIfPaymentIframe = () => {
        if (
            this.state.progress_phase === 'confirming' &&
            this.state.payment_method === 'online' &&
            this.state.payment_processor &&
            this.state.payment_processor.getFormType() === 'iframe'
        ) {
            this.props.scrollToBottomOfModal();
        }
    };

    onUpdateProgressPhase = () => {
        this.setState(
            {
                progress_phase: this.order.getProgressPhase(),
            },
            this.onProgressPhaseUpdated
        );
    };

    hidePaymentProcessingWarning = () => {
        this.setState({
            show_payment_processing_warning: false,
        });
    };

    showPaymentProcessingWarning = () => {
        if (this.showPaymentProcessingWarning.timeout) {
            clearTimeout(this.showPaymentProcessingWarning.timeout);
        }
        this.showPaymentProcessingWarning.timeout = setTimeout(() => {
            this.setState({
                show_payment_processing_warning: true,
            });
        }, 30000);
    };

    forceModalOpenUntilUnconfirmed = () => {
        if (this.modal_open_interval) {
            clearInterval(this.modal_open_interval);
            this.modal_open_interval = null;
        }

        this.modal_open_interval = setInterval(() => {
            if (this.state.progress_phase === 'unconfirmed') {
                clearInterval(this.modal_open_interval);
                this.modal_open_interval = null;

                if (this.state.progress_status === 'unconfirmable') {
                    this.props.closeModal();
                }
            } else if (!this.props.isModalOpen()) {
                this.props.openModal();
            }
        }, 100);
    };

    onUpdatePaymentMethod = () => {
        const payment_method = this.order.getPaymentMethod();
        this.setState(
            {
                payment_method: payment_method || '',
            },
            this.setPaymentProcessorAttributes
        );
    };

    onUpdateTotal = () => {
        this.setState({
            products: this.order.getProducts(),
            total: this.order.getTotal(),
            delivery_fee: this.order.getDeliveryFee(),
            discounts_total: this.order.getDiscountsTotal(),
            surcharge: this.order.getSurcharge(),
            tip: this.order.getTip(),
        });
    };

    onUpdateTaxTotals = () => {
        this.setState({
            tax_totals: this.order.getTaxTotals(),
        });
    };

    onUpdateExpectedInString = () => {
        this.setState({
            expected_in_string: this.order.getExpectedInString(),
        });
    };

    onUpdateMethod = () => {
        const method = this.order.getMethod();
        this.setState({
            method: method || '',
        });
    };

    onUpdateAddress = () => {
        this.setState({
            address: this.order.getAddress()
                ? this.order.getAddress().toString()
                : '',
        });
    };

    onUpdateExpectedAt = () => {
        this.setState({
            expected_at: this.order.getExpectedAt(),
            is_expected_today: this.order.isExpectedToday(),
        });
    };

    onUpdateMinutesToClosing = () => {
        this.setState({
            minutes_to_closing: this.order.getMinutesToClosing(),
        });
    };

    onUpdateLocation = () => {
        this.setState({
            location: this.order.getLocation(),
        });
    };

    onUpdateCredit = () => {
        this.setState({
            credit: this.order.getCredit(),
        });
    };

    onUpdatePaymentTotal = () => {
        this.setState({
            payment_total: this.order.getPaymentTotal(),
        });
    };

    onUpdateProducts = () => {
        this.setState({
            products: this.order.getProducts(),
        });
    };

    onUpdateComment = () => {
        this.setState({
            comment: this.order.getComment(),
        });
    };

    onCommentChange = comment => {
        this.order.setComment(comment);
    };

    onClose = () => {
        if (
            this.state.progress_phase === 'confirming' ||
            this.state.progress_phase === 'confirmed'
        ) {
            return;
        }
        this.props.closeModal();
    };

    onCustomerSubscribe = is_subscriber => {
        this.order.getCustomer().update({
            is_subscriber,
        });
    };

    onCancelClick = () => {
        if (this.state.progress_phase === 'confirming') {
            if (this.is_cancelling_confirmation) {
                return;
            }
            this.is_cancelling_confirmation = true;
            const { t } = this.props;

            if (
                this.state.payment_method === 'online' &&
                this.order.getPaymentProcessor() &&
                this.order.getPaymentProcessor().getFormType() === 'iframe'
            ) {
                Mobi2Go.UI.centeredAlert({
                    title: t('cancel_payment_title'),
                    body: t('cancel_payment_body'),
                    primary_button: t('cancel_payment_primary_button'),
                    secondary_button: t('cancel_payment_secondary_button'),
                    onClose: () => {
                        this.is_cancelling_confirmation = false;
                    },
                    onSecondaryButtonClick: () => {
                        this.is_cancelling_confirmation = true;
                        Mobi2Go.UI.centeredAlert({
                            title: t('cancelling_payment_title'),
                            body: t('cancelling_payment_body'),
                            footer: true,
                            primary_button: null,
                            temp: true,
                            temp_duration: this.order.getConfirmationCancellationCooldownDuration(),
                            show_progress_bar: true,
                            close_button: false,
                            onClose: () => {
                                this.is_cancelling_confirmation = true;
                                this.order.cancelConfirmation();
                            },
                        });
                        this.order.log(
                            'started cancelled confirmation attempt'
                        );
                    },
                });
            } else {
                Mobi2Go.UI.centeredAlert({
                    title: t('cancelling_order_title'),
                    body: t('cancelling_order_body'),
                    footer: true,
                    primary_button: null,
                    temp: true,
                    temp_duration: this.order.getConfirmationCancellationCooldownDuration(),
                    show_progress_bar: true,
                    close_button: false,
                    onClose: () => {
                        this.is_cancelling_confirmation = false;
                        this.order.cancelConfirmation();
                    },
                });
            }
        }
        if (this.state.progress_phase === 'unconfirmed') {
            this.props.closeModal();
        }
    };

    isPasswordEntered = () => {
        if (this.order_confirmation_payment) {
            return !!this.order_confirmation_payment.getTokenPassword();
        }
        return false;
    };

    fakeLoadingState = callback => {
        if (this.state.progress_phase !== 'unconfirmed') {
            return;
        }

        this.setState({ forced_into_loading_state: true }, callback);
    };

    resetFakeLoadingState = callback => {
        if (this.state.progress_phase !== 'unconfirmed') {
            return;
        }

        this.setState({ forced_into_loading_state: false }, callback);
    };

    validatePassword = () => {
        const password = this.order_confirmation_payment.getTokenPassword();

        if (password.length < 5) {
            this.promptToEnterPassword();
            return;
        }

        this.fakeLoadingState(() => {
            this.order.providePassword(password, is_valid => {
                this.resetFakeLoadingState();
                this.tokenVerificationCallback(is_valid);
            });
        });
    };

    validateFacebookReauthentication = authResponse => {
        if (!Customer.isLoggedIn()) {
            FB.api(
                '/me',
                { fields: ['name', 'email'] },
                async facebookAccount => {
                    const { userID, accessToken } = authResponse;
                    const { email } = facebookAccount;

                    const data = {
                        email,
                        facebookUserId: userID,
                        facebookAccessToken: accessToken,
                    };

                    this.fakeLoadingState(() => {
                        this.order.provideFacebookAuthentication(
                            data,
                            is_valid => {
                                this.resetFakeLoadingState();
                                this.tokenVerificationCallback(is_valid);
                            }
                        );
                    });
                }
            );
        }
    };

    validateAppleReauthentication = async authResponse => {
        const { authorization } = authResponse;

        this.fakeLoadingState(() => {
            this.order.provideAppleAuthentication(authorization, is_valid => {
                this.resetFakeLoadingState();
                this.tokenVerificationCallback(is_valid);
            });
        });
    };

    promptToEnterPassword = () => {
        // setupFacebook(this.validateFacebookReauthentication);
        // setupApple(this.validateAppleReauthentication);

        const { t } = this.props;
        Mobi2Go.UI.centeredAlert({
            title: t('saved_card_password_required_title'),
            body: t('saved_card_password_required_body'),
            onPrimaryButtonClick: () => {
                if (this.order_confirmation_payment) {
                    this.order_confirmation_payment.focusTokenPassword();
                }
            },
        });
    };

    onConfirmClick = () => {
        if (this.state.progress_phase === 'confirmed') {
            return;
        }

        if (
            this.state.payment_method === 'online' &&
            this.order.getPaymentTotal().toFloat() > 0 &&
            this.order.getPaymentCardToken() === null &&
            this.order.getPaymentProcessor() &&
            this.order.getPaymentProcessor().getFormType() === 'direct' &&
            !this.order.getPaymentProcessor().isCreditCardValid()
        ) {
            Mobi2Go.UI.trigger('invalidCreditCardDetailsWarning');
            return;
        }

        const { t } = this.props;

        // Closed warning
        if (
            this.state.minutes_to_closing <= 1 &&
            this.order.isExpectedToday()
        ) {
            const today_or_tonight =
                this.state.expected_at.format('H') >= 17
                    ? t('tonight')
                    : t('today');

            Mobi2Go.UI.centeredAlert({
                title: t('store_closed_title'),
                body: t('store_closed_body', { today_or_tonight }),
                primary_button: t('store_closed_primary_button'),
            });
            return;
        }

        if (
            this.order.getPaymentCardToken() &&
            this.order.isPasswordRequiredForPaymentToken() &&
            !this.order.isPasswordProvided()
        ) {
            if (this.isPasswordEntered()) {
                this.validatePassword();
                return;
            }
            this.promptToEnterPassword();
            return;
        }

        // Not expected today warning
        if (!this.state.is_expected_today) {
            let display_date;
            const { expected_at } = this.state;
            if (this.order.isExpectedTomorrow()) {
                display_date = t('expected_at_warning_tomorrow', {
                    time: t('common:time', { time: expected_at }),
                });
            } else {
                display_date = t('expected_at_warning_date', {
                    date: expected_at,
                    time: t('common:time', { time: expected_at }),
                });
            }

            Mobi2Go.UI.centeredAlert({
                title: t('scheduled_order_warning_title'),
                body: t('scheduled_order_warning_body', { display_date }),
                primary_button: t('scheduled_order_warning_primary_button'),
                onPrimaryButtonClick: this.showCardTokenModalOrConfirmation,
                secondary_button: t('scheduled_order_warning_secondary_button'),
            });
            return;
        }

        this.showCardTokenModalOrConfirmation();
    };

    showCardTokenModalOrConfirmation = () => {
        if (
            this.state.payment_method === 'online' &&
            this.order.getPaymentTotal().toFloat() > 0 &&
            !this.order.getPaymentCardToken() &&
            this.order.isPaymentCardTokenisationAvailable() &&
            !Customer.isGuest()
        ) {
            const { t } = this.props;
            Mobi2Go.UI.centeredAlert({
                id: 'save-card-prompt',
                title: t('save_card_title'),
                body: t('save_card_body'),
                primary_button: t('save_card_primary_button'),
                secondary_button: t('save_card_secondary_button'),
                onPrimaryButtonClick: () => {
                    this.order.setPaymentCardTokenSavingEnabled(true);
                    this.order.trigger('confirmClicked');
                    this.order.confirm();
                },
                onSecondaryButtonClick: () => {
                    this.order.setPaymentCardTokenSavingEnabled(false);
                    this.order.trigger('confirmClicked');
                    this.order.confirm();
                },
            });
            return;
        }
        this.order.trigger('confirmClicked');
        this.order.confirm();
    };

    onDoneClick = () => {
        if (this.state.progress_phase !== 'confirmed') {
            return;
        }
        this.order.finish();
        this.props.setModalIsClosable(true);
        this.props.closeModal();
    };

    focusOnConfirmButton = () => {
        if (this.order_confirmation_footer) {
            this.order_confirmation_footer.focusOnConfirmButton();
        }
    };

    tokenVerificationCallback(is_valid) {
        if (is_valid) {
            this.order.log('valid password provided to use card token');
            this.onConfirmClick();
        } else {
            this.order.log('invalid password provided to use card token');
            const { t } = this.props;
            Mobi2Go.UI.centeredAlert({
                title: t('saved_card_incorrect_password_title'),
                body: t('saved_card_incorrect_password_body'),
                onPrimaryButtonClick: () => {
                    if (this.order_confirmation_payment) {
                        this.order_confirmation_payment.focusTokenPassword();
                    }
                },
            });
        }
    }

    render() {
        if (this.order === null) {
            return null;
        }
        const { t, closeModal, openModal } = this.props;
        const { isGuest, progress_phase, isGiftCardEmpty, loyalty_program_with_gift_cards} = this.state;
        const { order } = this;
        let header_text = t(`${order.getMethod()}_header`);

        if (order.getProgressPhase() === 'confirmed') {
            header_text = t('confirmed_header');
        }

        return (
            <div>
                <ModalHeader onClose={this.onClose} headerText={header_text} />
                <CustomHtmlSlot slot="confirmation-above" />
                {progress_phase !== 'confirmed' && (
                    <div className="m2g-modal-body">
                        <OrderCustomer
                            name={this.state.customer_name}
                            phone_number={this.state.customer_phone_number}
                        />
                        {Mobi2Go.Locations.get().length > 1 && (
                            <OrderLocation location={this.state.location} />
                        )}
                        <OrderClosingWarning
                            minutes_to_closing={this.state.minutes_to_closing}
                            expected_at={this.state.expected_at}
                        />
                        {this.state.method === 'delivery' && (
                            <OrderAddress address={this.state.address} />
                        )}
                        <OrderTime
                            expected_at={this.state.expected_at}
                            is_expected_today={this.state.is_expected_today}
                            expected_in_string={this.state.expected_in_string}
                        />
                        <OrderItems
                            products={this.state.products}
                            delivery_fee={this.state.delivery_fee}
                            discounts_total={this.state.discounts_total}
                            surcharge={this.state.surcharge}
                            tip={this.state.tip}
                            total={this.state.total}
                            tax_totals={this.state.tax_totals}
                            currency_abrv={Mobi2Go.headoffice.currency_abrv}
                            service_fee={this.state.service_fee}
                        />
                        <CustomHtmlSlot slot="confirmation-middle" />
                        <OrderComment
                            comment={this.state.comment}
                            max_length={this.state.comment_max_length}
                            onChange={this.onCommentChange}
                        />
                        <OrderConfirmationPayment
                            ref={el => (this.order_confirmation_payment = el)}
                            progress_phase={progress_phase}
                            payment_method={this.state.payment_method}
                            payment_total={this.state.payment_total}
                            total={this.state.total}
                            method={this.state.method}
                            customer_credit_balance={
                                this.state.customer_credit_balance
                            }
                            credit={this.state.credit}
                            currency_abrv={Mobi2Go.headoffice.currency_abrv}
                            onCloseModal={() => this.props.closeModal()}
                            show_payment_processing_warning={
                                this.state.show_payment_processing_warning
                            }
                            location={this.state.location}
                            confirmClickHandler={this.onConfirmClick}
                        />
                        {progress_phase === 'unconfirmed' &&
                            !isGuest &&
                            (isGiftCardEmpty || loyalty_program_with_gift_cards) && (
                                <AddGiftCardButton
                                    onClick={() => {
                                        closeModal(true);
                                        changeToAddGiftCard(openModal);
                                    }}
                                    classNames={{
                                        button:
                                            'm2g-container m2g-button m2g-add-gift-card-button',
                                    }}
                                />
                            )}
                    </div>
                )}
                {progress_phase === 'confirmed' && (
                    <div className="m2g-modal-body">
                        <OrderConfirmationSuccess order={order} />
                    </div>
                )}

                <CustomHtmlSlot slot="confirmation-below" />
                <OrderConfirmationFooter
                    ref={el => (this.order_confirmation_footer = el)}
                    total={this.state.total}
                    progress_phase={progress_phase}
                    progress_status={this.state.progress_status}
                    payment_method={this.state.payment_method}
                    payment_processor={this.state.payment_processor}
                    forced_into_loading_state={
                        this.state.forced_into_loading_state
                    }
                    location={this.state.location}
                    method={this.state.method}
                    onCancelClick={this.onCancelClick}
                    onConfirmClick={this.onConfirmClick}
                    onDoneClick={this.onDoneClick}
                />
            </div>
        );
    }
}

export default forwardRefWithTranslation('order.confirmation')(
    OrderConfirmation
);
